'use client';

import {useQuery, type UseQueryResult} from '@tanstack/react-query';
import {tenantKeys} from './tenantKeys';
import {getMfaApi} from '../api/mfa-api';
import {useApiToken} from '@/matic/hooks/useApiClient';

type TenantSettings = {
  mfaRequired: boolean;
  cuiEnabled: boolean;
  qmsRecordsEnabled: boolean;
};

/**
 * Fetch tenant settings (MFA requirement, CUI enabled, etc.)
 *
 * Wraps the fetchTenantSettings API call in a reusable TanStack Query hook
 * with a 5-minute stale time to avoid excessive refetches.
 *
 * @returns UseQueryResult<TenantSettings, Error>
 *
 * @example
 * ```typescript
 * const {data: settings, isLoading} = useTenantSettings();
 *
 * if (settings?.cuiEnabled) {
 *   // Show CUI-related UI
 * }
 * ```
 */
export function useTenantSettings(): UseQueryResult<TenantSettings> {
  const token = useApiToken();
  const api = getMfaApi(token);

  return useQuery({
    queryKey: tenantKeys.settings(),
    queryFn: async () => api.fetchTenantSettings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: Boolean(token),
  });
}
